import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ApiError, MessageService } from '../../../../../../core';
import { CurrencyFormatPipe, DecimalFormatPipe } from '../../../../../../shared';
import { createTooltipColorIcon, textStyleDefault, tooltipDefault } from '../../../../../dashboard/charts';
import { ModalidadeComercial, RepresentanteKPIFilter } from '../../../kpis/adapters';
import { RepresentanteKPIsHttpService } from '../../../kpis/services';
import { RepresentanteModalidadeComercialFaturamentoWidgetState } from './representante-modalidade-comercial-faturamento-widget.state';
import { RepresentanteModalidadeComercialFaturamentoWidgetStateService } from './representante-modalidade-comercial-faturamento-widget.state.service';

@Component({
    selector: 'sgt-representante-modalidade-comercial-faturamento-widget',
    templateUrl: './representante-modalidade-comercial-faturamento-widget.component.html',
    styleUrls: ['./representante-modalidade-comercial-faturamento-widget.component.less'],
    providers: [
        RepresentanteModalidadeComercialFaturamentoWidgetStateService
    ]
})
export class RepresentanteModalidadeComercialFaturamentoWidgetComponent implements OnInit {
    /** ID do widget */
    @Input() id: string;
    /** Title do widget */
    @Input() title = 'Faturamento Por Modalidade Comercial';
    /** Subtitle do widget */
    @Input() subtitle: string = null;
    /** State do widget */
    state: RepresentanteModalidadeComercialFaturamentoWidgetState;
    /** Subscription do state do widget */
    private state$: Subscription;
    /** Flag que indica se está carregando o widget */
    isLoading$: Observable<boolean>;

    // Pie Chart
    @ViewChild('pieChartContainer', {static: true}) pieChartContainer: ElementRef;
    chart: any;
    isChartResizeRegistered = false;
    resizeNamespaceChart = 'resize.sgt-representante-modalidade-comercial-faturamento-widget';

    constructor(private messageService: MessageService,
                private currencyFormatPipe: CurrencyFormatPipe,
                private decimalFormatPipe: DecimalFormatPipe,
                private stateService: RepresentanteModalidadeComercialFaturamentoWidgetStateService,
                private representanteKPIsHttpService: RepresentanteKPIsHttpService) { }

    ngOnInit(): void {
        this.initState();
        this.createPieChart();
    }

    /** Inicializando o state do component */
    private initState() {
        // State
        this.state$ = this.stateService.init(false).subscribe(state => this.state = state);
        // IsLoading
        this.isLoading$ = this.stateService.isLoading$;
    }

    /** Carrega o widget */
    load(filter: RepresentanteKPIFilter) {
        try {
            this.stateService.setIsLoading(true);
            this.representanteKPIsHttpService.getModalidadesComerciaisFaturamento(filter)
                .pipe(take(1))
                .subscribe(
                    data => {
                        this.stateService.setData(data);
                        setTimeout(() => this.afterLoad(), 0);
                    },
                    (error: ApiError) => this.messageService.error(error.message, error.cause, error),
                    () => this.stateService.setIsLoading(false)
                );
        } catch (error) {
            this.messageService.error('Tente atualizar a página e se o erro persistir entre em contato com o suporte', `Erro ao carregar os KPI's de faturamento das modalidades comerciais`, error);
        }
    }

    /** Após carregar o widget */
    private afterLoad() {
        // Pie Chart
        this.show(this.state.data.modalidadesComerciais);
    }

    /** Cria o pie chart com as participações das modalidades comerciais */
    private createPieChart() {
        if (this.chart && !this.chart.isDisposed()) {
            return;
        }

        this.chart = echarts.init(this.pieChartContainer.nativeElement);
        this.chart.setOption({
            textStyle: textStyleDefault,
            toolbox: {
                show: false
            },
            tooltip : {
                trigger: 'item',
                appendToBody: true,
                padding: [5, 10],
                backgroundColor: tooltipDefault.backgroundColor,
                formatter: (params) => {
                    const value = params.value && !isNaN(params.value) ? Number(params.value) : 0;
                    const percent = params.percent && !isNaN(params.percent) ? Number(params.percent) : 0;
                    return `${createTooltipColorIcon(params?.color)} ${params?.name} - ${this.currencyFormatPipe.transform(value, 1, 2, 2)} (${this.decimalFormatPipe.transform(percent, 1, 2, 2)}%)`;
                },
                textStyle: tooltipDefault.textStyle
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                textStyle: {
                    fontSize: 10
                },
                pageIconSize: 10,
                pageTextStyle: {
                    fontSize: 10
                },
                data: [],
            },
            series : [
                {
                    name: 'sgt-representante-modalidade-comercial-faturamento-widget',
                    type: 'pie',
                    selectedMode: 'single',
                    radius: ['40%', '65%'],
                    center: ['50%', '45%'],
                    label: {
                        normal: {
                            formatter: (params) => {
                                const value = params.value && !isNaN(params.value) ? Number(params.value) : 0;
                                const percent = params.percent && !isNaN(params.percent) ? Number(params.percent) : 0;
                                return `${this.currencyFormatPipe.transform(value, 1, 2, 2)}\n${this.decimalFormatPipe.transform(percent, 1, 2, 2)}%`;
                            }
                        },
                    },
                    data: [],
                }
            ]
        });

        // Somente registra o evento do resize uma única vez
        if (!this.isChartResizeRegistered) {
            this.isChartResizeRegistered = true;
            jQuery(window).on(this.resizeNamespaceChart, () => {
                setTimeout(() => {
                    if (this.chart && !this.chart.isDisposed()) {
                        this.chart.resize();
                    }
                }, 200);
            });
        }
    }

    /** Exibe o chart com a participação da carteira positiva por quantidade de clientes */
    show(modalidesComerciais: ModalidadeComercial[]) {
        if (modalidesComerciais) {
            const legendData = [];
            const seriesData = [];

            modalidesComerciais.forEach(modalidadeComercial => {
                legendData.push(modalidadeComercial.nome);
                seriesData.push({
                    name: modalidadeComercial.nome,
                    value: modalidadeComercial.faturamento?.valor ?? 0,
                    itemStyle: {
                        normal: {color: modalidadeComercial.color}
                    }
                } as any);
            });



             this.chart.setOption({
                legend: {
                    data: legendData
                },
                series: [
                    {
                        name: 'sgt-representante-modalidade-comercial-faturamento-widget',
                        data: seriesData,
                    }
                ]
            });
        } else {
            // this.clearPieChart();
        }
    }

    ngOnDestroy(): void {
        if (this.chart) {
            this.chart.dispose();
        }

        // Removendo o evento do resize do window
        jQuery(window).off(this.resizeNamespaceChart);

        this.state$?.unsubscribe();
    }
}
